import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/pages/Auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "@phosphor-icons/react";

const strong = (p) => p.length >= 10 && /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) { setSent(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Shell title="Forgot your password?" sub="Enter your email and we'll send a link to choose a new one.">
      {sent ? (
        <div className="mt-8 bg-white rounded-3xl border border-border p-6" data-testid="forgot-sent">
          <CheckCircle size={40} weight="fill" className="text-[#10B981]" />
          <p className="mt-4 font-semibold">{sent}</p>
          <p className="mt-2 text-sm text-muted-foreground">The link works once and expires in 60 minutes. Check your spam folder too.</p>
          <Link to="/login"><Button variant="outline" className="mt-6 w-full h-12 rounded-full font-bold" data-testid="forgot-back">Back to sign in</Button></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
          <div><Label className="text-sm font-bold">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-xl" data-testid="forgot-email" /></div>
          <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="forgot-submit">
            {busy ? "Sending…" : "Send reset link"}</Button>
          <Link to="/login" className="block text-sm font-bold text-muted-foreground hover:text-[#3B82F6]">← Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong(pw.password)) return setError("Password must be at least 10 characters with an uppercase letter, a lowercase letter and a number.");
    if (pw.password !== pw.confirm) return setError("The two passwords do not match.");
    setBusy(true); setError("");
    try { await api.post("/auth/reset-password", { token, password: pw.password }); setDone(true); }
    catch (err) { setError(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  if (done)
    return (
      <Shell title="Password updated" sub="">
        <div className="mt-8 bg-white rounded-3xl border border-border p-6" data-testid="reset-done">
          <CheckCircle size={40} weight="fill" className="text-[#10B981]" />
          <p className="mt-4 font-semibold">Your new password is saved. Any older reset links no longer work.</p>
          <Button onClick={() => nav("/login")} className="mt-6 w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="reset-to-login">Sign in</Button>
        </div>
      </Shell>
    );

  return (
    <Shell title="Choose a new password" sub="Pick something you don't use anywhere else.">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
        <div><Label className="text-sm font-bold">New password</Label>
          <Input type="password" required autoComplete="new-password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="reset-password" />
          <p className="mt-2 text-xs text-muted-foreground">At least 10 characters with an uppercase letter, a lowercase letter and a number.</p></div>
        <div><Label className="text-sm font-bold">Confirm new password</Label>
          <Input type="password" required autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-2 h-12 rounded-xl" data-testid="reset-confirm" /></div>
        {error && <p className="text-sm font-semibold text-[#EF4444]" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="reset-submit">
          {busy ? "Saving…" : "Save new password"}</Button>
      </form>
    </Shell>
  );
}
